import React, { useState, useEffect, useCallback } from 'react';
import { Course, Instructor, Module, Lab, FAQ, Differentiator } from '../../types';
import TrackBadge from '../../components/TrackBadge';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useTracks } from '../../hooks/useTracks';

const emptyModule: Module = { title: '', topics: [''] };
const emptyLab: Lab = { title: '', description: '' };
const emptyFaq: FAQ = { question: '', answer: '' };
const emptyDifferentiator: Differentiator = { title: '', description: '' };

const emptyCourse: Omit<Course, 'id'> = {
  title: '',
  shortDescription: '',
  fullDescription: '',
  track: '',
  certificationAlignment: [],
  licensingRequirements: '',
  prerequisites: [''],
  skillLevel: 'Intermediate',
  duration: '',
  delivery: 'Live Online (Microsoft Teams)',
  labEnvironment: '',
  instructorId: '',
  curriculum: [{ ...emptyModule }],
  labs: [],
  faqs: [],
  whyThisCourse: '',
  whatYouWillLearn: [''],
  whoShouldAttend: [''],
  differentiators: [],
  active: true,
};

const ensureStringArray = (value: unknown, fallback: string[] = ['']): string[] =>
  Array.isArray(value) && value.length > 0
    ? value.map((item) => (typeof item === 'string' ? item : ''))
    : [...fallback];

const ensureString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const ensureBoolean = (value: unknown, fallback = true): boolean =>
  typeof value === 'boolean' ? value : fallback;

const ensureModuleArray = (value: unknown): Module[] =>
  Array.isArray(value) && value.length > 0
    ? value.map((module) => ({
        title:
          module && typeof module === 'object' && 'title' in module && typeof module.title === 'string'
            ? module.title
            : '',
        topics:
          module && typeof module === 'object' && 'topics' in module
            ? ensureStringArray(module.topics)
            : [''],
      }))
    : [{ ...emptyModule }];

const ensureLabArray = (value: unknown): Lab[] =>
  Array.isArray(value)
    ? value.map((lab) => ({
        title:
          lab && typeof lab === 'object' && 'title' in lab && typeof lab.title === 'string'
            ? lab.title
            : '',
        description:
          lab && typeof lab === 'object' && 'description' in lab && typeof lab.description === 'string'
            ? lab.description
            : '',
      }))
    : [];

const ensureFaqArray = (value: unknown): FAQ[] =>
  Array.isArray(value)
    ? value.map((faq) => ({
        question:
          faq && typeof faq === 'object' && 'question' in faq && typeof faq.question === 'string'
            ? faq.question
            : '',
        answer:
          faq && typeof faq === 'object' && 'answer' in faq && typeof faq.answer === 'string'
            ? faq.answer
            : '',
      }))
    : [];

// Differentiators are stored as {title, description} objects in courses.json.
// Some legacy/corrupted records hold bare strings (description-only) — recover
// them by treating the string as the description so editing doesn't lose data.
// Returns an empty array when the field is missing/empty (matches Labs/FAQs).
const ensureDifferentiatorArray = (value: unknown): Differentiator[] =>
  Array.isArray(value)
    ? value.map((item) => {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          const obj = item as { title?: unknown; description?: unknown };
          return {
            title: typeof obj.title === 'string' ? obj.title : '',
            description: typeof obj.description === 'string' ? obj.description : '',
          };
        }
        if (typeof item === 'string') {
          return { title: '', description: item };
        }
        return { ...emptyDifferentiator };
      })
    : [];

// Some legacy records have whoShouldAttend as a single plain string. Wrap it
// into a 1-element array so the form has something to show, instead of falling
// back to [''] and silently dropping the real text.
const ensureAudienceArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return ensureStringArray(value);
  if (typeof value === 'string' && value.trim().length > 0) return [value];
  return [''];
};

const normalizeCourseForForm = (course: Course): Omit<Course, 'id'> => ({
  ...emptyCourse,
  ...course,
  title: ensureString(course.title),
  shortDescription: ensureString(course.shortDescription),
  fullDescription: ensureString(course.fullDescription),
  track: ensureString(course.track),
  licensingRequirements: ensureString(course.licensingRequirements),
  skillLevel: ensureString(course.skillLevel, emptyCourse.skillLevel),
  duration: ensureString(course.duration),
  delivery: ensureString(course.delivery, emptyCourse.delivery),
  labEnvironment: ensureString(course.labEnvironment),
  instructorId: ensureString(course.instructorId),
  whyThisCourse: ensureString(course.whyThisCourse),
  certificationAlignment: ensureStringArray(course.certificationAlignment),
  prerequisites: ensureStringArray(course.prerequisites),
  curriculum: ensureModuleArray(course.curriculum),
  labs: ensureLabArray(course.labs),
  faqs: ensureFaqArray(course.faqs),
  whatYouWillLearn: ensureStringArray(course.whatYouWillLearn),
  whoShouldAttend: ensureAudienceArray(course.whoShouldAttend),
  differentiators: ensureDifferentiatorArray(course.differentiators),
  active: ensureBoolean(course.active, true),
});

const ManageCourses: React.FC = () => {
  usePageTitle('Manage Courses');
  const [courses, setCourses] = useState<Course[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Course | null>(null);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState<Omit<Course, 'id'>>(emptyCourse);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const authFetch = useAuthFetch();
  const tracks = useTracks();
  const trackOptions = tracks.map((track) => track.name);

  const fetchData = useCallback(async () => {
    try {
      const [coursesRes, instructorsRes] = await Promise.all([
        authFetch('/api/courses'),
        authFetch('/api/instructors'),
      ]);
      if (coursesRes.ok) setCourses(await coursesRes.json());
      if (instructorsRes.ok) setInstructors(await instructorsRes.json());
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);


  const handleEdit = (course: Course) => {
    try {
      const normalized = normalizeCourseForForm(course);
      setEditing(course);
      setCreating(false);
      setFormData(normalized);
      setError('');
    } catch (err) {
      console.error('Error preparing course for editing:', err);
      setError('Failed to load course for editing. Please try again.');
    }
  };

  const handleCreate = () => {
    setCreating(true);
    setEditing(null);
    setFormData({ ...emptyCourse, track: trackOptions[0] ?? '' });
    setError('');
  };

  const handleCancel = () => {
    setEditing(null);
    setCreating(false);
    setError('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const url = editing ? `/api/courses/${editing.id}` : '/api/courses';
      const method = editing ? 'PUT' : 'POST';
      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        await fetchData();
        handleCancel();
      } else {
        const data = await res.json();
        setError(data.message || 'Failed to save course');
      }
    } catch {
      setError('An error occurred while saving');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (courseId: string) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    try {
      const res = await authFetch(`/api/courses/${courseId}`, { method: 'DELETE' });
      if (res.ok) fetchData();
    } catch (err) {
      console.error('Error deleting course:', err);
    }
  };

  const handleToggleActive = async (course: Course) => {
    try {
      await authFetch(`/api/courses/${course.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...course, active: !course.active }),
      });
      fetchData();
    } catch (err) {
      console.error('Error toggling course:', err);
    }
  };

  const updateArrayField = (
    field: 'prerequisites' | 'whatYouWillLearn' | 'whoShouldAttend' | 'certificationAlignment',
    index: number,
    value: string
  ) => {
    setFormData((prev) => {
      const arr = [...(prev[field] as string[])];
      arr[index] = value;
      return { ...prev, [field]: arr };
    });
  };

  const addArrayItem = (field: 'prerequisites' | 'whatYouWillLearn' | 'whoShouldAttend' | 'certificationAlignment') => {
    setFormData((prev) => ({ ...prev, [field]: [...(prev[field] as string[]), ''] }));
  };

  const removeArrayItem = (field: 'prerequisites' | 'whatYouWillLearn' | 'whoShouldAttend' | 'certificationAlignment', index: number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: (prev[field] as string[]).filter((_, i) => i !== index),
    }));
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading courses...</p>
      </div>
    );
  }

  if (editing || creating) {
    return (
      <div className="admin-form-page">
        <section className="dashboard-header">
          <div className="section-container">
            <h1 className="page-title">{editing ? 'Edit Course' : 'Create Course'}</h1>
          </div>
        </section>
        <section className="section">
          <div className="section-container">
            {error && <div className="form-error">{error}</div>}
            <div className="admin-form">
              <div className="form-grid">
                <div className="form-group form-full">
                  <label className="form-label">Title *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.title}
                    onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Track *</label>
                  <select
                    className="form-input"
                    value={formData.track}
                    onChange={(e) => setFormData((p) => ({ ...p, track: e.target.value }))}
                  >
                    {/* Keep a course's existing track selectable even if it is no
                        longer in the managed list, instead of silently reassigning */}
                    {formData.track && !trackOptions.includes(formData.track) && (
                      <option value={formData.track}>{formData.track}</option>
                    )}
                    {trackOptions.map((track) => (
                      <option key={track} value={track}>
                        {track}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Skill Level</label>
                  <select
                    className="form-input"
                    value={formData.skillLevel}
                    onChange={(e) => setFormData((p) => ({ ...p, skillLevel: e.target.value }))}
                  >
                    <option value="Beginner">Beginner</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Duration</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.duration}
                    onChange={(e) => setFormData((p) => ({ ...p, duration: e.target.value }))}
                    placeholder="e.g., 4 days"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Delivery</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.delivery}
                    onChange={(e) => setFormData((p) => ({ ...p, delivery: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Lab Environment</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.labEnvironment}
                    onChange={(e) => setFormData((p) => ({ ...p, labEnvironment: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Instructor</label>
                  <select
                    className="form-input"
                    value={formData.instructorId}
                    onChange={(e) => setFormData((p) => ({ ...p, instructorId: e.target.value }))}
                  >
                    <option value="">Select instructor</option>
                    {instructors.map((inst) => (
                      <option key={inst.id} value={inst.id}>{inst.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Short Description *</label>
                  <textarea
                    className="form-input form-textarea"
                    value={formData.shortDescription}
                    onChange={(e) => setFormData((p) => ({ ...p, shortDescription: e.target.value }))}
                    rows={2}
                  />
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Full Description</label>
                  <textarea
                    className="form-input form-textarea"
                    value={formData.fullDescription}
                    onChange={(e) => setFormData((p) => ({ ...p, fullDescription: e.target.value }))}
                    rows={4}
                  />
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Why This Course</label>
                  <textarea
                    className="form-input form-textarea"
                    value={formData.whyThisCourse}
                    onChange={(e) => setFormData((p) => ({ ...p, whyThisCourse: e.target.value }))}
                    rows={3}
                  />
                </div>
                <div className="form-group form-full">
                  <label className="form-label">Licensing Requirements</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.licensingRequirements}
                    onChange={(e) => setFormData((p) => ({ ...p, licensingRequirements: e.target.value }))}
                  />
                </div>
              </div>

              {/* Array fields */}
              {(['certificationAlignment', 'whatYouWillLearn', 'whoShouldAttend', 'prerequisites'] as const).map((field) => (
                <div key={field} className="form-array-section">
                  <label className="form-label">{field.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}</label>
                  {(Array.isArray(formData[field]) ? (formData[field] as string[]) : []).map((item, idx) => (
                    <div key={idx} className="form-array-row">
                      <input
                        type="text"
                        className="form-input"
                        value={item}
                        onChange={(e) => updateArrayField(field, idx, e.target.value)}
                      />
                      <button className="btn btn-text btn-danger" onClick={() => removeArrayItem(field, idx)}>Remove</button>
                    </div>
                  ))}
                  <button className="btn btn-text" onClick={() => addArrayItem(field)}>+ Add</button>
                </div>
              ))}

              {/* Curriculum */}
              <div className="form-array-section">
                <label className="form-label">Curriculum Modules</label>
                {(Array.isArray(formData.curriculum) ? formData.curriculum : []).map((mod, mIdx) => (
                  <div key={mIdx} className="form-module">
                    <div className="form-module-header">
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Module title"
                        value={mod.title}
                        onChange={(e) => {
                          const updated = [...formData.curriculum];
                          updated[mIdx] = { ...updated[mIdx], title: e.target.value };
                          setFormData((p) => ({ ...p, curriculum: updated }));
                        }}
                      />
                      <button
                        className="btn btn-text btn-danger"
                        onClick={() => setFormData((p) => ({ ...p, curriculum: p.curriculum.filter((_, i) => i !== mIdx) }))}
                      >
                        Remove Module
                      </button>
                    </div>
                    {(Array.isArray(mod.topics) ? mod.topics : []).map((topic, tIdx) => (
                      <div key={tIdx} className="form-array-row form-indent">
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Topic"
                          value={topic}
                          onChange={(e) => {
                            const updated = [...formData.curriculum];
                            const topics = [...updated[mIdx].topics];
                            topics[tIdx] = e.target.value;
                            updated[mIdx] = { ...updated[mIdx], topics };
                            setFormData((p) => ({ ...p, curriculum: updated }));
                          }}
                        />
                        <button
                          className="btn btn-text btn-danger"
                          onClick={() => {
                            const updated = [...formData.curriculum];
                            updated[mIdx] = { ...updated[mIdx], topics: updated[mIdx].topics.filter((_, i) => i !== tIdx) };
                            setFormData((p) => ({ ...p, curriculum: updated }));
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      className="btn btn-text form-indent"
                      onClick={() => {
                        const updated = [...formData.curriculum];
                        updated[mIdx] = { ...updated[mIdx], topics: [...updated[mIdx].topics, ''] };
                        setFormData((p) => ({ ...p, curriculum: updated }));
                      }}
                    >
                      + Add Topic
                    </button>
                  </div>
                ))}
                <button
                  className="btn btn-text"
                  onClick={() => setFormData((p) => ({ ...p, curriculum: [...p.curriculum, { ...emptyModule }] }))}
                >
                  + Add Module
                </button>
              </div>

              {/* Labs */}
              <div className="form-array-section">
                <label className="form-label">Labs</label>
                {(Array.isArray(formData.labs) ? formData.labs : []).map((lab, idx) => (
                  <div key={idx} className="form-module">
                    <div className="form-grid">
                      <div className="form-group">
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Lab title"
                          value={lab.title}
                          onChange={(e) => {
                            const updated = [...formData.labs];
                            updated[idx] = { ...updated[idx], title: e.target.value };
                            setFormData((p) => ({ ...p, labs: updated }));
                          }}
                        />
                      </div>
                      <div className="form-group">
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Lab description"
                          value={lab.description}
                          onChange={(e) => {
                            const updated = [...formData.labs];
                            updated[idx] = { ...updated[idx], description: e.target.value };
                            setFormData((p) => ({ ...p, labs: updated }));
                          }}
                        />
                      </div>
                    </div>
                    <button
                      className="btn btn-text btn-danger"
                      onClick={() => setFormData((p) => ({ ...p, labs: p.labs.filter((_, i) => i !== idx) }))}
                    >
                      Remove Lab
                    </button>
                  </div>
                ))}
                <button className="btn btn-text" onClick={() => setFormData((p) => ({ ...p, labs: [...p.labs, { ...emptyLab }] }))}>
                  + Add Lab
                </button>
              </div>

              {/* Differentiators */}
              <div className="form-array-section">
                <label className="form-label">Differentiators</label>
                {(Array.isArray(formData.differentiators) ? formData.differentiators : []).map((diff, idx) => (
                  <div key={idx} className="form-module">
                    <div className="form-grid">
                      <div className="form-group">
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Differentiator title"
                          value={diff.title}
                          onChange={(e) => {
                            const updated = [...formData.differentiators];
                            updated[idx] = { ...updated[idx], title: e.target.value };
                            setFormData((p) => ({ ...p, differentiators: updated }));
                          }}
                        />
                      </div>
                      <div className="form-group">
                        <textarea
                          className="form-input form-textarea"
                          placeholder="Differentiator description"
                          value={diff.description}
                          rows={3}
                          onChange={(e) => {
                            const updated = [...formData.differentiators];
                            updated[idx] = { ...updated[idx], description: e.target.value };
                            setFormData((p) => ({ ...p, differentiators: updated }));
                          }}
                        />
                      </div>
                    </div>
                    <button
                      className="btn btn-text btn-danger"
                      onClick={() => setFormData((p) => ({ ...p, differentiators: p.differentiators.filter((_, i) => i !== idx) }))}
                    >
                      Remove Differentiator
                    </button>
                  </div>
                ))}
                <button
                  className="btn btn-text"
                  onClick={() => setFormData((p) => ({ ...p, differentiators: [...p.differentiators, { ...emptyDifferentiator }] }))}
                >
                  + Add Differentiator
                </button>
              </div>

              {/* FAQs */}
              <div className="form-array-section">
                <label className="form-label">FAQs</label>
                {(Array.isArray(formData.faqs) ? formData.faqs : []).map((faq, idx) => (
                  <div key={idx} className="form-module">
                    <div className="form-grid">
                      <div className="form-group">
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Question"
                          value={faq.question}
                          onChange={(e) => {
                            const updated = [...formData.faqs];
                            updated[idx] = { ...updated[idx], question: e.target.value };
                            setFormData((p) => ({ ...p, faqs: updated }));
                          }}
                        />
                      </div>
                      <div className="form-group">
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Answer"
                          value={faq.answer}
                          onChange={(e) => {
                            const updated = [...formData.faqs];
                            updated[idx] = { ...updated[idx], answer: e.target.value };
                            setFormData((p) => ({ ...p, faqs: updated }));
                          }}
                        />
                      </div>
                    </div>
                    <button
                      className="btn btn-text btn-danger"
                      onClick={() => setFormData((p) => ({ ...p, faqs: p.faqs.filter((_, i) => i !== idx) }))}
                    >
                      Remove FAQ
                    </button>
                  </div>
                ))}
                <button className="btn btn-text" onClick={() => setFormData((p) => ({ ...p, faqs: [...p.faqs, { ...emptyFaq }] }))}>
                  + Add FAQ
                </button>
              </div>

              <div className="form-group">
                <label className="form-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData((p) => ({ ...p, active: e.target.checked }))}
                  />
                  Active (visible in catalog)
                </label>
              </div>

              <div className="form-actions">
                <button className="btn btn-outline" onClick={handleCancel}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : editing ? 'Update Course' : 'Create Course'}
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="admin-manage-page">
      <section className="dashboard-header">
        <div className="section-container">
          <div className="section-header-row">
            <h1 className="page-title">Manage Courses</h1>
            <button className="btn btn-primary" onClick={handleCreate}>+ Create Course</button>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="section-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Track</th>
                <th>Level</th>
                <th>Duration</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {courses.map((course) => (
                <tr key={course.id}>
                  <td className="td-title">{course.title}</td>
                  <td><TrackBadge track={course.track} size="small" /></td>
                  <td>{course.skillLevel}</td>
                  <td>{course.duration}</td>
                  <td>
                    <span className={`status-badge ${course.active ? 'status-active' : 'status-cancelled'}`}>
                      {course.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="td-actions">
                    <button className="btn btn-text" onClick={() => handleEdit(course)}>Edit</button>
                    <button className="btn btn-text" onClick={() => handleToggleActive(course)}>
                      {course.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button className="btn btn-text btn-danger" onClick={() => handleDelete(course.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {courses.length === 0 && <p className="empty-text">No courses found. Create your first course.</p>}
        </div>
      </section>
    </div>
  );
};

export default ManageCourses;
